import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  FormGroup,
  Input,
  Spinner,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import { saveReturnDetails, fetchOrderReturn } from '../../actions/orderActions';
import toast, { Toaster } from 'react-hot-toast';

const CreateCstReturnWarehouse = () => {
  const [customText, setCustomText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [creatingCstId, setCreatingCstId] = useState(null); // Track which CST is being created
  const [editedQty, setEditedQty] = useState({}); // Track edited qty per item
  const [lastQuery, setLastQuery] = useState(''); // Prevent duplicate requests
  const dispatch = useDispatch();
  let userID = '';
  if (localStorage.getItem('authUser')) {
    const userObj = JSON.parse(localStorage.getItem('authUser'));
    if (userObj?.role_id !== undefined) {
      userID = userObj?.user_id;
    }
  }
  const fetchDetails = async (query) => {
    // Prevent duplicate or repeated requests
    if (loading || query === lastQuery) return;
    setLoading(true);
    // Do not clear result here to avoid UI flicker
    try {
      // Await the response and set it to result for display
      const data = await fetchOrderReturn({
        params: { tracking_id: query
           
         },
        dispatch, 
      });
      setResult(data); // <-- Set the API response to result state
     // setLastQuery(query); // Set lastQuery only after successful fetch
    } catch (err) {
      setResult({ error: 'Failed to fetch details.' });
    }
    setLoading(false);
  };

  const handleSearch = () => {
    const trimmed = customText.trim();
    // Only fetch if not loading and not already fetched for this query
    if (trimmed && trimmed !== lastQuery && !loading) {
      fetchDetails(trimmed);
    }
  };

  // New function to handle CST creation API call and reload
  const handleCreateCST = async (item, sku, qty) => {
    setCreatingCstId(item); // Set the current CST being created
    try {
      const [awbNo] = item.split('-');
      const response = await saveReturnDetails({
        params: {
          tracking_id: awbNo,
          sku: sku,
          qty: qty || 1,
         user_id: userID, // Use edited qty or default to 1
        },
        dispatch,
      });
      setResult(response); // Display the response in the UI
      toast.success('CST Created Successfully!', {
        style: {
          border: '1px solid #fff',
          padding: '20px',
          color: '#ffffff',
          background: '#4CAF50',
        },
        iconTheme: {
          primary: '#FFFAEE',
          secondary: '#4CAF50',
        },
      });
      setCustomText(""); // Clear the search box
      setResult(null);   // Optionally clear the result
      setEditedQty({}); // Clear edited qty after creation
    } catch (err) {
      toast.error('Failed to create CST');
    }
    setCreatingCstId(null); // Reset after done
  };

  const handleQtyChange = (sku, value) => {
    if (value < 1) return;
    setEditedQty(prev => ({
      ...prev,
      [sku]: value
    }));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl={9}>
              {/* ...Breadcrumb or other header elements... */}
            </Col>
            <Col>
              <FormGroup className="position-relative mb-3">
                <Input
                  type="text"
                  className="form-control"
                  placeholder="Scan AWB Number"
                  value={customText}
                  onChange={e => setCustomText(e.target.value)}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  style={{ fontWeight: 500, fontSize: 18, letterSpacing: 1 }}
                />
                <Button
                  color="primary"
                  className="position-absolute top-0 end-0"
                  style={{ top: 0, right: 0, fontWeight: 600 }}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="bx bx-search-alt align-middle"></i>
                  )}
                </Button>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              {loading && (
                <div className="my-3 text-center">
                  <Spinner color="primary" />
                  <div style={{ fontSize: 14, color: "#888" }}>Loading details...</div>
                </div>
              )}
              {result && (
                <Card className="my-3 shadow border-0">
                  <CardHeader style={{ background: "#e3f2fd" }} className="text-dark">
                    <CardTitle tag="h5" className="mb-0 d-flex align-items-center">
                      <i className="bx bx-package me-2"></i>
                      Order Item Details
                      {result.OrderDetails &&
                        result.OrderDetails[0] &&
                        result.OrderDetails[0].OrderNo && (
                          <span style={{
                            marginLeft: "auto",
                            fontWeight: 500,
                            fontSize: 15,
                            color: "#1976d2"
                          }}>
                            Order No: {result.OrderDetails[0].OrderNo}
                          </span>
                        )
                      }
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    {result.error ? (
                      <div className="text-danger">{result.error}</div>
                    ) : result.code !== 100 ? (
                      <div className="text-danger">{result.message || 'No data found.'}</div>
                    ) : (
                      result.OrderDetails &&
                      result.OrderDetails[0] &&
                      result.OrderDetails[0].orderitems &&
                      result.OrderDetails[0].orderitems.length > 0 ? (
                        <div className="d-flex flex-wrap gap-3">
                          {result.OrderDetails[0].orderitems.map((item, idx) => {
                            const qtyToShow = editedQty[item.Sku] || item.Qty;
                            return (
                              <Card key={item.Id || idx} className="mb-2 flex-grow-1 shadow-sm" style={{ minWidth: 300, maxWidth: 350 }}>
                                <CardBody>
                                  <div style={{ fontWeight: 600, fontSize: 16, color: "#222" }}>
                                    {item.Name}
                                  </div>
                                  <div style={{ fontSize: 14, color: "#555", marginBottom: 8 }}>
                                    <div><b>Order No:</b> {item.OrderRef}</div>
                                    <div><b>SKU:</b> {item.Sku}</div>
                                    <div>
                                      <b>Qty:</b>
                                      {item.Qty > 1 ? (
                                        <>
                                          <Input
                                            type="number"
                                            min={1}
                                            max={item.Qty}
                                            value={qtyToShow}
                                            onChange={e => handleQtyChange(item.Sku, Number(e.target.value))}
                                            style={{ width: 70, display: 'inline-block', marginLeft: 8 }}
                                            size="sm"
                                          />
                                          <span style={{ marginLeft: 8, color: "#888" }}>(Edit Qty)</span>
                                        </>
                                      ) : (
                                        <span> {item.Qty}</span>
                                      )}
                                    </div>
                                    <div><b>AWB No:</b> <span style={{ color: "#007bff" }}>{item.awbNo}</span></div>
                                    <div><b>Status:</b> <span style={{ color: "#28a745" }}>{item.LsStatus}</span></div>
                                  </div>
                                  <div className="text-end">
                                    {String(item.LsStatus).toLowerCase() !== 'delivered' ? (
                                      <Button
                                        color="danger"
                                        size="sm"
                                        className="mt-2"
                                        style={{ fontWeight: 600, letterSpacing: 1 }}
                                        disabled
                                      >
                                        <i className="bx bx-block"></i> Not Delivered
                                      </Button>
                                    ) : (
                                      String(item.ReturnEligible).toLowerCase() === 'pending' ? (
                                        <Button
                                          color="success"
                                          size="sm"
                                          className="mt-2"
                                          style={{ fontWeight: 600, letterSpacing: 1 }}
                                          onClick={() => handleCreateCST(item.awbNo + '-' + item.Sku, item.Sku, qtyToShow)}
                                          disabled={creatingCstId === (item.awbNo + '-' + item.Sku)}
                                        >
                                          {creatingCstId === (item.awbNo + '-' + item.Sku) ? (
                                            <>
                                              <Spinner size="sm" className="me-1" /> Creating CST...
                                            </>
                                          ) : (
                                            <><i className="bx bx-plus"></i> Create CST</>
                                          )}
                                        </Button>
                                      ) : (
                                        <Button
                                          color="secondary"
                                          size="sm"
                                          className="mt-2"
                                          style={{ fontWeight: 600, letterSpacing: 1 }}
                                          disabled
                                        >
                                          <i className="bx bx-check"></i> CST Created
                                        </Button>
                                      )
                                    )}
                                  </div>
                                </CardBody>
                              </Card>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-warning">No items found for this AWB number.</div>
                      )
                    )}
                  </CardBody>
                </Card>
              )}
              {/* Remove custom confirm box */}
            </Col>
          </Row>
          {/* ...existing code... */}
        </Container>
      </div>
      {/* ...Toaster or other components... */}
       <Toaster position="top-right" reverseOrder={false} />
    </React.Fragment>
  );
};

export default CreateCstReturnWarehouse;
                       